import { FC } from 'react';
import styled from 'styled-components';
import Avatar from '../Avatar';
import RestorativeTask from '../RestorativeTask';
import DateStatus from './DateStatus';
import TaskStatus from './TaskStatus';
import Restorative, { RestorativeProgram, DateSummary } from '../../services/Restorative';
import { ReactComponent as PencilIcon } from '../../assets/icons/pencil.svg';
import AccessibleHiddenText from '../AccessibleHiddenText';
import { RestorativeResidentData } from '../RestorativeResidentDialog/RestorativeResidentDialog';
import { ReactComponent as WarningCircleIcon } from '../../assets/icons/warning-circle.svg';
import Tooltip from '../Tooltip';

const NameHead = styled.th(({ theme }) => {
  const { color, type } = theme.new;

  return `
    color: ${color.shade[2]};
    font-size: ${type.size[14]};
    line-height: 1.375rem;
    font-weight: ${type.weight.medium};
    padding: 0.5rem;
    background-color: transparent;
  `;
});

const NameWrap = styled.span`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  gap: 0.625rem;
  align-items: center;
`;

const TaskWrap = styled.span`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 0.4375rem;
`;

const StatusCell = styled.td`
  padding: 0.9375rem 0;
`;

const PencilText = styled.div(({ theme }) => {
  return `
    font-size: ${theme.new.type.size[14]};
    color: ${theme.new.color.neutral[1]};
    display: none;
  `;
});

const ErrorTile = styled.div(({ theme }) => {
  const { color } = theme.new;

  return `
    background-color: ${color.neutral[2]};
    border-radius: 6px;
    padding: 0.875rem;
  `;
});

const EditButton = styled.button(({ theme }) => {
  const { color } = theme.new;

  return `
    background-color: transparent;
    height: 3.125rem;
    width: 3.125rem;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    border-radius: 6px;

    &:hover {
      background-color: ${color.neutral[7]};

      svg {
        height: 1.25rem;
        width: 1.25rem;
  
        path {
          stroke: ${color.neutral[1]};
          fill: ${color.neutral[6]};
        }
      }


      div {
        div {
          display: block;
        }
      }
    }

    svg {
      height: 1.25rem;
      width: 1.25rem;
      margin-left: 4px;
      path {
        stroke: ${color.neutral[8]};
      }
    }
  `;
});

const ErrorCell = styled.td`
  padding: 0 6px;
`;

interface ResidentRowProps {
  id: number;
  name: string;
  programs: RestorativeProgram[];
  dateSummaries: DateSummary[];
  status: number;
  logClickCallback: (playerId: number, name: string, date: Date) => void;
  editClickCallback: (playerId: RestorativeResidentData) => void;
}

const ResidentRow: FC<ResidentRowProps> = ({
  id,
  name,
  programs,
  dateSummaries,
  status,
  logClickCallback,
  editClickCallback,
}) => {
  const showProgramWarning = Restorative.getProgramConflicts(programs);

  const programLoop = programs.map((program) => {
    return (
      <RestorativeTask
        key={`programId-${program.id}`}
        programId={program.id}
        time={program.todays_duration ? Math.floor(program.todays_duration / 60) : 0}
      >
        {program.name}
      </RestorativeTask>
    );
  });

  const dateSummariesLoop = dateSummaries.map((summary, index) => {
    const handleLogClick = () => {
      logClickCallback(id, name, summary.date);
    };

    return (
      <td key={`dateSummary-${index}`}>
        <DateStatus complete={summary.complete} tasks={summary.tasks} callback={handleLogClick} />
      </td>
    );
  });

  const handleEditClick = () => {
    const firstName = name.split(' ')[0];
    const lastName = name.split(' ')[1];
    editClickCallback({ id, firstName, lastName, programs });
  };

  return (
    <tr>
      <NameHead scope="row">
        <NameWrap>
          <Avatar name={name} small /> {name}
        </NameWrap>
      </NameHead>

      <td>
        <TaskWrap>{programLoop}</TaskWrap>
      </td>

      <ErrorCell>
        {showProgramWarning && (
          <>
            <ErrorTile data-tooltip-id={`programConflict-${id}`} data-tooltip-delay-hide={200} tabIndex={0}>
              <WarningCircleIcon />
            </ErrorTile>

            <Tooltip id={`programConflict-${id}`} maxWidth="25vw">
              Please note that combining Active ROM and Passive ROM Programs, does not count as an active day for
              billing purposes
            </Tooltip>
          </>
        )}
      </ErrorCell>

      <td>
        <EditButton onClick={() => handleEditClick()}>
          <div>
            <PencilIcon />
            <PencilText>Edit</PencilText>
          </div>

          <AccessibleHiddenText>Edit resident</AccessibleHiddenText>
        </EditButton>
      </td>

      <td></td>

      {dateSummariesLoop}

      <td></td>
      <StatusCell>
        <TaskStatus current={status} max={7} />
      </StatusCell>
    </tr>
  );
};

export default ResidentRow;
